import { Auth0Client } from "@auth0/auth0-spa-js";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { io, Socket } from "socket.io-client";
import { getAuthToken } from "../api/authUtils";
import { apiConfig, auth0Config } from "../config";
import { QueryKeys } from "../constants/queryKeys";
import { useOrganizationReference } from "../pages/facility/hooks/useOrganization";
import { useSelector } from "../store";
import { UserNotification } from "../types/notification";
import useSettings from "./useSettings";

// eslint-disable-next-line import/no-mutable-exports
export let socket: Socket = null;

const useSocketIO = () => {
  const [isConnected, setIsConnected] = useState(!!socket?.connected);
  const organizationReference = useOrganizationReference();
  const queryClient = useQueryClient();
  const { settings } = useSettings();
  const userProfile = useSelector((state) => state.user.profile);

  function joinRoom(organization: string) {
    console.info("joining room", organization);
    socket.emit("join-room", organization);
  }

  function joinUserRoom(userId: string) {
    console.log("joining user room", userId);
    socket.emit("join-user-room", userId);
  }

  function onConnect() {
    console.info("connected to socket", socket.id);
    setIsConnected(true);
  }

  function onDisconnect() {
    console.info("socket disconnected", socket.id);
    setIsConnected(false);
  }

  const onEvents = useCallback(
    (data: string[][], clientId: string) => {
      console.info("refreshing event", data);
      if (clientId && clientId === settings.clientId) {
        return;
      }
      data.forEach((queryKey) => {
        // Some backends embed the full response payload as a JSON-stringified
        // last element so the frontend can update the cache without a refetch.
        const lastElement = queryKey[queryKey.length - 1];
        if (queryKey.length > 2 && lastElement.startsWith("{")) {
          try {
            const embeddedData = JSON.parse(lastElement);
            queryClient.setQueryData(queryKey.slice(0, -1), embeddedData);
            return;
          } catch {
            // fall through to standard invalidation
          }
        }
        queryClient.invalidateQueries({ queryKey });
      });
    },
    [settings.clientId, queryClient],
  );

  function onUserNotification(notification: UserNotification) {
    console.log("received user notification", notification);

    // Optimistically update React Query cache
    const userId = userProfile?.id;
    if (userId) {
      // Update notifications list cache
      queryClient.setQueriesData(
        { queryKey: [QueryKeys.PUSH_NOTIFICATIONS] },
        (oldData: UserNotification[] | undefined) => {
          if (!oldData) return [notification];
          const exists = oldData.some((n) => n.id === notification.id);
          if (!exists) {
            return [notification, ...oldData];
          }
          return oldData;
        },
      );

      // Update unread count cache if notification is unread
      if (!notification.read) {
        queryClient.setQueriesData(
          { queryKey: [QueryKeys.PUSH_NOTIFICATION_COUNTS, userId] },
          (oldCount: number | undefined) => (oldCount ?? 0) + 1,
        );
      }
    }

    // Invalidate to ensure consistency with server
    queryClient.invalidateQueries({ queryKey: [QueryKeys.PUSH_NOTIFICATIONS] });
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.PUSH_NOTIFICATION_COUNTS],
    });
  }

  useEffect(() => {
    const connectSocket = async () => {

      if (!userProfile) {
        console.info("User not authenticated, skipping socket connection");
        return;
      }

      if (!socket) {
        try {
          // Create Auth0 client to get authentication token
          const auth0Client = new Auth0Client({
            redirect_uri: window.location.origin,
            ...auth0Config,
          });

          const token = await getAuthToken(auth0Client);

          // Create socket with authentication token
          socket = io(`${apiConfig.notificationServiceBase}/events`, {
            transports: ["websocket"],
            reconnectionAttempts: 10,
            auth: {
              token: token,
            },
          });
        } catch (error) {
          console.error("Failed to authenticate WebSocket connection:", error);
          return;
        }
      }

      socket.on("connect", onConnect);
      socket.on("disconnect", onDisconnect);
      socket.on("events", onEvents);
      socket.on("user-notification", onUserNotification);
    };

    connectSocket();

    return () => {
      socket?.off("connect", onConnect);
      socket?.off("disconnect", onDisconnect);
      socket?.off("events", onEvents);
      socket?.off("user-notification", onUserNotification);
    };
  }, [onEvents, userProfile]);

  useEffect(() => {
    if (organizationReference?.identifier && isConnected) {
      joinRoom(organizationReference.identifier);
    }
  }, [organizationReference, isConnected]);

  useEffect(() => {
    if (userProfile?.authId && isConnected) {
      // Extract user ID from authId (format: "auth0|userId")
      const userId = userProfile.authId.split("|")[1] || userProfile.authId;
      joinUserRoom(userId);
    }
  }, [userProfile, isConnected]);

  return {
    isConnected,
  };
};

export default useSocketIO;
